import { defa }               from 'defa-object';
import ConfigView             from 'scripts/defa/basic/view-config-view';
import LogEntries             from 'scripts/defa/log/collection-log-entries';
import LogEntry               from 'scripts/defa/log/model-log-entry';
import LogEntryCollectionView from 'scripts/defa/log/view-log-entry-collection-view';
import LogEntryCreateForm     from 'scripts/defa/log/view-log-entry-create-form';
import LogTagsForm            from 'scripts/defa/log/view-log-tags-form';
import PaginationStrip        from 'scripts/plugins/pagination-strip';
import Container              from 'scripts/poppet/container';
import template               from 'templates/logContainer.jst.html';

/**
 * defa.views.LogContainer
 * A layout for holding log interface.
 * @version 1.2.1
 */

const api = {
	logs: defa.vars.apiHost + '/logs',
};

const channelLayout = Bb.Radio.channel( 'layout' ),
	channelNav    = Bb.Radio.channel( 'nav' );

const channel = Bb.Radio.channel( 'objects' );

const selectors = {
	regionCreate   : '[data-region=log-create]',
	regionTags     : '[data-region=log-tags]',
	regionContainer: '[data-region=log-container]',
	buttonCreate   : '[data-click=log-create]',
	buttonTags     : '[data-click=log-tags]',
	
	pagination: '[data-element=pagination-strip]',
};

const LogContainer = Container.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template: template,
	
	ui: {
		create   : selectors.regionCreate,
		tags     : selectors.regionTags,
		container: selectors.regionContainer,
		btnTags  : selectors.buttonTags,
		btnCreate: selectors.buttonCreate,
	},
	
	events: {
		'click @ui.btnTags'  : 'onClickBtnTags',
		'click @ui.btnCreate': 'onClickBtnCreate',
	},
	
	regions: {
		'create'   : '@ui.create',
		'tags'     : '@ui.tags',
		'container': '@ui.container',
	},
	
	behaviors: {
		ViewAnimator: {},
	},
	
	templateContext: function () {
		return {
			context : this.options.type,
			logTitle: this.logTitle(),
		};
	},
	
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	/**
	 * Triggers when a user clicks the tags button.
	 * @void
	 */
	onClickBtnTags: function ( event ) {
		event.preventDefault();
		this._showLogTags();
	},
	
	/**
	 * Triggers when a user clicks the create button.
	 * @void
	 */
	onClickBtnCreate: function ( event ) {
		event.preventDefault();
		this._showLogEntryCreate();
	},
	
	/**
	 * Triggers when a new LogEntry is saved.
	 * @void
	 */
	onLogEntrySync: function ( model ) {
		model.set( 'fresh', true );
		this.logEntries.add( model, { at: 0 } );
		
		this.getRegion( 'create' ).currentView.animateOut();
	},
	
	/**
	 * Triggers when the LogTagsForm submits a new set of tags.
	 * @param {array} tags  An array of tags for a new query.
	 * @void
	 */
	onChangeTags: function ( tags ) {
		
		this.page = 0;
		this.tags = tags;
		
		this._setUrl();
		this.getLogEntries();
		
		this.getRegion( 'tags' ).currentView.animateOut();
	},
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	/**
	 * Triggers before the layout is painted in the DOM.
	 * @void
	 */
	attachView: function () {
		
		// set default tags based on log type
		switch ( this.options.type ) {
			
			case 'user'    :
				this.tags = [ 'SUPPORT_ENQUIRIES', ];
				break;
			
			case 'billing' :
				this._showLogEntryCreate();
				this.tags = [ 'SUPPORT_ENQUIRIES', ];
				break;
			
			case 'device'  :
				this._showLogEntryCreate();
				this.tags = [ 'SUPPORT_ENQUIRIES', ];
				break;
			
		}
		
		this.logEntries = new LogEntries();
		this.getLogEntries();
	},
	
	/**
	 * Gets a title for the current log based on its logtype
	 * @return {string}  A string representing which object this is the log for.
	 */
	logTitle: function () {
		
		switch ( this.options.type ) {
			
			case 'billing' :
				return channel.request( 'customers' ).get( this.options.bid ).name();
			
			case 'device' :
				return channel.request( 'units' ).get( this.options.bid ).get( 'alias' );
			
			case 'user' :
				return channel.request( 'users' ).get( this.options.bid ).get( 'email' );
		}
		
	},
	
	/**
	 * Sets the current url based on set log variables.
	 * @void
	 */
	_setUrl: function () {
		
		channelNav.trigger( 'log', [ this.options.type, this.options.bid, ( parseInt( this.page ) + 1 ), ], {} );
	},
	
	/**
	 * Fetches a page of log entries and initiates the painting of said log entry collection.
	 * @void
	 */
	getLogEntries: function () {
		
		let self = this,
			logType;
		
		switch ( self.options.type ) {
			
			case 'billing' :
				logType = 'customer';
				break;
			
			case 'device' :
				logType = 'unit';
				break;
			
			default :
				logType = self.options.type;
				break;
		}
		
		let configView = new ConfigView( {
			context: 'wait',
		} );
		this.showChildView( 'container', configView );
		
		this.page = this.page || this.options.page;
		
		$.get( {
			 url : api.logs,
			 data: {
				 type: logType,
				 id  : this.options.bid,
				 page: this.page,
				 tags: this.tags.join( ',' ),
			 }
		 } )
		 .done( function ( response ) {
			
			 // set vars
			 self.page = response.currentPage;
			 self.tags = response.tags;
			
			 // update views
			 self._showPagination( response );
			 self._showLogEntries( response.results );
			
		 } )
		 .fail( function ( response ) {
			 channelLayout.trigger( 'error', response.responseJSON );
		 } );
		
	},
	
	// visual functions
	
	/**
	 * Attaches the log entry view.
	 * @void
	 */
	_showLogEntryCreate: function () {
		
		let logEntry = new LogEntry();
		
		let logEntryCreateForm = new LogEntryCreateForm( {
			model: logEntry,
			type : this.options.type,
			bid  : this.options.bid,
		} );
		
		this.listenToOnce( logEntry, 'sync', this.onLogEntrySync );
		
		this.showChildView( 'create', logEntryCreateForm, false );
		
	},
	
	/**
	 * Shows the LogTagsForm for a log.
	 * @void
	 */
	_showLogTags: function () {
		
		let self = this;
		
		let logTagsForm = new LogTagsForm( {
			tags: self.tags,
		} );
		
		self.listenTo( logTagsForm, 'change:tags', self.onChangeTags );
		
		self.showChildView( 'tags', logTagsForm, false );
		
	},
	
	/**
	 * Shows the pagination strip for an options object.
	 * @param {object} options  Object containing the currentPage and totalPages integers.
	 * @void
	 */
	_showPagination: function ( options ) {
		let self = this;
		
		// only show pagination if there are two or more totalPages
		if ( options.totalPages >= 2 ) {
			
			// create new pagination strip
			if ( !self.paginationStrip ) {
				
				self.paginationStrip = new PaginationStrip( selectors.pagination, options );
				
				$( selectors.pagination ).on( 'change', function ( value, id ) {
					self.page = id;
					
					self._setUrl();
					self.getLogEntries();
				} );
				
				// update existing pagination strip
			} else {
				self.paginationStrip.settings( options );
			}
		}
	},
	
	/**
	 * Paints the collection of log entries.
	 * @param {array} results  An array of LogEntries
	 * @void
	 */
	_showLogEntries: function ( results ) {
		this.logEntries.set( results );
		
		let logEntryCollectionView = new LogEntryCollectionView( {
			collection: this.logEntries,
		} );
		
		this.showChildView( 'container', logEntryCollectionView );
	},
	
} );

export default LogContainer;